// frontend/src/components/favorites/FavoriteGroupModal.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  Button, Autocomplete, CircularProgress, Box, Chip
} from '@mui/material';
import debounce from 'lodash.debounce';
import api from '../../services/api';
import favoritesService from '../../services/favoritesService';
import { useNotification } from '../../context/NotificationContext';

const FavoriteGroupModal = ({ open, onClose, onSave, group }) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  const [signerOptions, setSignerOptions] = useState([]);
  const [loadingSigners, setLoadingSigners] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (group) {
      setGroupName(group.nome);
      setSelectedUsers(group.assinantes);
    } else {
      setGroupName('');
      setSelectedUsers([]);
    }
  }, [group]);

  const fetchSigners = async (searchTerm) => {
    if (!searchTerm) { setSignerOptions([]); return; }
    setLoadingSigners(true);
    try {
      const response = await api.get(`/usuarios/?search=${searchTerm}`);
      const currentIds = new Set(selectedUsers.map(u => u.id));
      const filteredOptions = response.data.filter(u => !currentIds.has(u.id));
      setSignerOptions(filteredOptions);
    } catch (error) { console.error("Falha ao buscar assinantes", error); }
    finally { setLoadingSigners(false); }
  };
  
  const debouncedFetchSigners = useMemo(() => debounce(fetchSigners, 500), [selectedUsers]);

  const handleSubmit = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) {
      showWarning('O grupo precisa de um nome e pelo menos um assinante.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      nome: groupName,
      assinante_ids: selectedUsers.map(u => u.id),
    };

    try {
      if (group) {
        await favoritesService.update(group.id, payload);
        showSuccess('Grupo favorito atualizado com sucesso!');
      } else {
        await favoritesService.create(payload);
        showSuccess('Grupo favorito criado com sucesso!');
      }
      onSave();
      onClose();
    } catch (error) {
      showError('Falha ao salvar o grupo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{group ? 'Editar Grupo Favorito' : 'Criar Novo Grupo Favorito'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nome do Grupo"
          type="text"
          fullWidth
          variant="outlined"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          options={signerOptions}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.nome_completo || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedUsers}
          onChange={(event, newValue) => setSelectedUsers(newValue)}
          onInputChange={(event, newInputValue) => debouncedFetchSigners(newInputValue)}
          loading={loadingSigners}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Adicionar Assinantes"
              placeholder="Busque por nome, CPF..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (<>{loadingSigners ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option.nome_completo} {...getTagProps({ index })} />
            ))
          }
        />
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24}/> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FavoriteGroupModal;